// Demo-only map. Real data, real screening results.
//
// Hooks up to the same data the authed app would render — substations
// and major grid from /api/v1/demo/layers/* — plus the result set
// produced by replaying a pre-captured engine run (demoFixture.json)
// via demoApi's demoReplayStream. No placeholder cloud, no synthetic
// funnel; the "limit" is at the perimeter (filters locked in
// DemoControls, click-through goes to LockedAnalysisPreview, deep
// zoom blurs).

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Map, {
  Source,
  Layer,
  Marker,
  NavigationControl,
  Popup,
} from 'react-map-gl';
import { demoSubstations, demoGrid220kv, demoPorts } from './demoApi';
import { track } from './demoTelemetry';

// Separate token from the authed app (SPEC_DEMO.md Risk #7). Falls back
// to the main token in dev so engineers don't need to provision two
// just to run npm dev.
const MAPBOX_TOKEN =
  import.meta.env?.VITE_DEMO_MAPBOX_TOKEN ||
  import.meta.env?.VITE_MAPBOX_TOKEN ||
  '';

// Initial framing: centred over Helgeland (between Mosjøen and Mo i
// Rana) at a zoom that comfortably fits the demo bbox the backend
// clamps to. The auto-fitBounds on the first `complete` event then
// snaps to the exact result cluster.
const NORWAY_CENTER = { longitude: 13.7, latitude: 66.0, zoom: 6.3 };
const MAX_ZOOM_BEFORE_BLUR = 9; // SPEC_DEMO.md Decision #7

// NVE Nettanlegg4 — the public WMS the authed app loads via
// frontend/src/config/layers.js → "power_lines_no". Renders official
// NVE line geometry + names (Distribusjonsnett, Sjokabler,
// Regionalnett, Transmisjonsnett) straight from kart.nve.no. Mapbox
// GL JS substitutes {bbox-epsg-3857} per tile, so we just hand it the
// templated URL. No auth, no ingestion, identical visual to prod.
const NVE_NETTANLEGG_WMS_URL =
  'https://kart.nve.no/enterprise/services/Nettanlegg4/MapServer/WMSServer' +
  '?service=WMS&version=1.1.1&request=GetMap' +
  '&layers=Distribusjonsnett,Sjokabler,Regionalnett_luftledning46171,Transmisjonsnett_luftledning43179' +
  '&styles=&format=image/png&transparent=true' +
  '&srs=EPSG:3857&width=256&height=256&bbox={bbox-epsg-3857}';

// Visual hierarchy — viable sites dominate, everything else is context.
// The principle: the eye should land on the result dots within 1 second
// of the map rendering. Context layers (substations, grid, lines, ports)
// are deliberately dimmed so they prove the engine is using real data
// without competing for attention.

// Substations: small, faint, no stroke — they're context, not features.
const SUBSTATION_PAINT = {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 2, 8, 3, 10, 4],
  'circle-color': '#94a3b8',
  'circle-opacity': 0.30,
  'circle-stroke-width': 0,
};
// 220 kV+ grid nodes: small grey dots, no ring. Previously a loud
// amber-ringed circle that competed with the result dots.
const GRID_PAINT = {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 2, 8, 3, 10, 4],
  'circle-color': '#cbd5e1',
  'circle-opacity': 0.35,
  'circle-stroke-width': 0,
};
// Ports: kept visible because they're rare and meaningful in Norway,
// but slightly less bright than the result teal so they don't compete.
const PORT_PAINT = {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 3, 8, 5, 12, 8],
  'circle-color': '#0ea5e9',
  'circle-opacity': 0.7,
  'circle-stroke-width': 0,
};

const DemoMap = forwardRef(function DemoMap(
  {
    results = [],
    onSiteClick,
    hoveredIdx = -1,         // sidebar list hover → highlight corresponding marker
    onHoverIdx,              // map marker hover → highlight corresponding sidebar row
  },
  externalRef,
) {
  const mapRef = useRef(null);
  useImperativeHandle(externalRef, () => ({
    flyTo: (opts) => mapRef.current?.flyTo?.(opts),
  }));

  const [viewState, setViewState] = useState(NORWAY_CENTER);
  const [substationsFC, setSubstationsFC] = useState(null);
  const [gridFC, setGridFC] = useState(null);
  const [portsFC, setPortsFC] = useState(null);

  // Background layer fetch — fire-and-forget; a failure here downgrades
  // the visual but doesn't break the demo. Real screening results still
  // overlay correctly even if any of these layers are missing. Each is
  // settled independently so a slow / 5xx response from one doesn't
  // block the others. Power lines come from NVE's WMS directly (see
  // NVE_NETTANLEGG_WMS_URL above), not from our backend.
  useEffect(() => {
    let cancelled = false;
    const safe = (fn, setter, label) =>
      fn()
        .then((fc) => { if (!cancelled) setter(fc); })
        .catch((e) => console.warn(`[demo] ${label} unavailable:`, e?.message || e));
    safe(demoSubstations, setSubstationsFC, 'substations');
    safe(demoGrid220kv,   setGridFC,        'grid');
    safe(demoPorts,       setPortsFC,       'ports');
    return () => { cancelled = true; };
  }, []);

  // Build the screening-results FeatureCollection. Backend's demo
  // path strips per-site fields to a small whitelist (site_label, lat,
  // lon, country, nearest_grid_*), so we don't have a stable id — use
  // an index-derived key.
  const resultsFC = useMemo(() => {
    if (!results?.length) return null;
    return {
      type: 'FeatureCollection',
      features: results
        .filter((r) => typeof r.lat === 'number' && typeof r.lon === 'number')
        .map((r, i) => ({
          type: 'Feature',
          id: `result-${i}`,
          properties: {
            idx: i,
            name: r.site_label || `Site ${i + 1}`,
            voltage_kv: r.nearest_grid_voltage_kv,
            substation: r.nearest_grid_name,
            distance_km: r.nearest_grid_distance_km,
            operator: r.nearest_grid_operator || null,
          },
          geometry: { type: 'Point', coordinates: [r.lon, r.lat] },
        })),
    };
  }, [results]);

  // Auto-snap to the result set whenever a fresh screening lands. This
  // is the psychologically-powerful "we found your answer" moment —
  // wide Helgeland view collapses to a tight bbox around the
  // 4 viable sites.
  useEffect(() => {
    if (!resultsFC?.features?.length) return;
    const coords = resultsFC.features.map((f) => f.geometry.coordinates);
    if (coords.length === 1) {
      mapRef.current?.flyTo?.({
        center: coords[0],
        zoom: MAX_ZOOM_BEFORE_BLUR - 0.5,   // close enough to read parcel-level context
        speed: 1.0,
        essential: true,
      });
      return;
    }
    let minLon =  Infinity, minLat =  Infinity;
    let maxLon = -Infinity, maxLat = -Infinity;
    for (const [lon, lat] of coords) {
      if (lon < minLon) minLon = lon;
      if (lat < minLat) minLat = lat;
      if (lon > maxLon) maxLon = lon;
      if (lat > maxLat) maxLat = lat;
    }
    mapRef.current?.fitBounds?.(
      [[minLon, minLat], [maxLon, maxLat]],
      // Top padding leaves room for the LockedAnalysisPreview card.
      // maxZoom only just under the blur threshold so a tight cluster
      // (all 4 sites within ~5 km) still separates visually — at the
      // old cap of 7.5 they'd stack on top of each other.
      { padding: { top: 80, bottom: 80, left: 60, right: 60 },
        maxZoom: MAX_ZOOM_BEFORE_BLUR - 0.5, duration: 1100 },
    );
  }, [resultsFC]);

  const zoomLocked = viewState.zoom > MAX_ZOOM_BEFORE_BLUR;
  const mapStyle = zoomLocked
    ? 'mapbox://styles/mapbox/satellite-streets-v12'
    : 'mapbox://styles/mapbox/light-v11';

  // Fire telemetry exactly once per blur transition.
  const lastZoomLocked = useRef(false);
  useEffect(() => {
    if (zoomLocked && !lastZoomLocked.current) {
      track('zoom_blur_triggered', { zoom: Number(viewState.zoom.toFixed(2)) });
    }
    lastZoomLocked.current = zoomLocked;
  }, [zoomLocked, viewState.zoom]);

  // Hover state for the *context* layers only (substations / grid /
  // ports). The result markers below own their own hover relationship
  // with the sidebar's ranked list (in DemoSearchProgress) — see the
  // hoveredIdx-driven popup further down.
  const [hover, setHover] = useState(null);

  function handleMouseMove(e) {
    const feat = e.features?.[0];
    if (!feat) { setHover(null); return; }
    const p = feat.properties || {};
    let label = null;
    if (feat.layer?.id === 'demo-substations') {
      const kv = p.voltage_level ? `${p.voltage_level} kV` : '—';
      label = { title: p.name || 'Substation', sub: `Substation · ${kv}` };
    } else if (feat.layer?.id === 'demo-grid') {
      const kv = p.voltage_level ? `${p.voltage_level} kV` : '—';
      label = { title: p.name || 'Transmission point', sub: `Transmission · ${kv}` };
    } else if (feat.layer?.id === 'demo-ports') {
      const type = p.port_type || p.system_name || 'port';
      label = {
        title: p.name || 'Port',
        sub: `${type}${p.municipality ? ' · ' + p.municipality : ''}`,
      };
    }
    if (!label) { setHover(null); return; }
    setHover({ lon: e.lngLat.lng, lat: e.lngLat.lat, ...label });
  }

  // Derive the popup for the currently-hovered result. This handles
  // BOTH directions of cross-hover with a single source: hovering a
  // marker calls onHoverIdx → parent sets state → flows back here;
  // hovering a sidebar row goes through the same path. Either way,
  // hoveredIdx is the input.
  const resultPopup = useMemo(() => {
    if (hoveredIdx < 0 || hoveredIdx >= results.length) return null;
    const r = results[hoveredIdx];
    if (!r || typeof r.lat !== 'number' || typeof r.lon !== 'number') return null;
    const lines = [];
    const kv = r.nearest_grid_voltage_kv;
    const km = r.nearest_grid_distance_km;
    if (kv && km != null) lines.push(`${kv} kV substation · ${Number(km).toFixed(1)} km away`);
    else if (kv)          lines.push(`${kv} kV substation`);
    else if (km != null)  lines.push(`${Number(km).toFixed(1)} km to grid`);
    if (r.nearest_grid_name) {
      const op = r.nearest_grid_operator ? ` (${r.nearest_grid_operator})` : '';
      lines.push(`Connects to ${r.nearest_grid_name}${op}`);
    }
    return {
      lon: r.lon,
      lat: r.lat,
      title: `#${hoveredIdx + 1} · ${r.site_label || 'Viable site'}`,
      sub: lines.join('\n'),
    };
  }, [hoveredIdx, results]);

  // Popup shown over the map: prefer the result hover (it's the
  // primary value-prop), fall back to context-layer hover.
  const activePopup = resultPopup || hover;

  function zoomOut() {
    track('cta_clicked', { location: 'zoom_lock_zoom_out' });
    mapRef.current?.flyTo?.({
      zoom: Math.min(viewState.zoom, MAX_ZOOM_BEFORE_BLUR - 1),
      duration: 600,
    });
  }

  if (!MAPBOX_TOKEN) {
    return (
      <div className="demo-map-fallback">
        <p>Map unavailable — VITE_DEMO_MAPBOX_TOKEN not set in this build.</p>
      </div>
    );
  }

  return (
    <div className="demo-map-container">
      <div className={'demo-map-wrap' + (zoomLocked ? ' map-zoom-locked' : '')}>
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(e) => setViewState(e.viewState)}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHover(null)}
          cursor={activePopup ? 'pointer' : 'grab'}
          mapStyle={mapStyle}
          mapboxAccessToken={MAPBOX_TOKEN}
          interactiveLayerIds={[
            'demo-substations',
            'demo-grid',
            'demo-ports',
          ]}
          maxBounds={[[2, 56], [33, 72]]}
        >
          <NavigationControl position="top-right" showCompass={false} />

          {/* Power lines — NVE Nettanlegg4 as a WMS raster overlay,
              same source the authed app uses (see frontend/src/config/
              layers.js → "power_lines_no"). Renders the official NVE
              line styling + names (Distribusjonsnett, Sjokabler,
              Regionalnett, Transmisjonsnett) directly from kart.nve.no
              — no ingestion, no synthetic geometry. minzoom=6 to match
              prod; opacity 0.85 so the basemap labels stay readable. */}
          <Source id="demo-nve-grid" type="raster" tiles={[NVE_NETTANLEGG_WMS_URL]} tileSize={256}>
            <Layer
              id="demo-nve-grid"
              type="raster"
              minzoom={6}
              paint={{ 'raster-opacity': 0.40 }}
            />
          </Source>

          {gridFC && (
            <Source id="demo-grid" type="geojson" data={gridFC}>
              <Layer id="demo-grid" type="circle" paint={GRID_PAINT} />
            </Source>
          )}

          {substationsFC && (
            <Source id="demo-substations" type="geojson" data={substationsFC}>
              <Layer id="demo-substations" type="circle" paint={SUBSTATION_PAINT} />
            </Source>
          )}

          {portsFC && (
            <Source id="demo-ports" type="geojson" data={portsFC}>
              <Layer id="demo-ports" type="circle" paint={PORT_PAINT} />
            </Source>
          )}

          {/* Numbered result markers — HTML so we can put "1, 2, 3, 4"
              inside each dot. #1 renders bigger + brighter than the
              rest; hovering a sidebar list row scales the matching
              marker up (and vice-versa via onHoverIdx). DOM events
              are cheaper than circle-layer features here because we
              only have ≤6 results. */}
          {resultsFC?.features.map((f, i) => {
            const [lon, lat] = f.geometry.coordinates;
            const p = f.properties;
            const isTop = i === 0;
            const isHovered = hoveredIdx === i;
            const cls =
              'demo-result-marker'
              + (isTop ? ' is-top' : '')
              + (isHovered ? ' is-hovered' : '');
            return (
              <Marker
                key={`result-${i}`}
                longitude={lon}
                latitude={lat}
                anchor="center"
                style={{ cursor: 'pointer' }}
              >
                <div
                  className={cls}
                  onMouseEnter={() => onHoverIdx?.(i)}
                  onMouseLeave={() => onHoverIdx?.(-1)}
                  onClick={(e) => {
                    e.stopPropagation();
                    const site = results[i];
                    if (site) onSiteClick?.(site);
                  }}
                >
                  <span className="demo-result-marker-halo" aria-hidden="true" />
                  <span className="demo-result-marker-num">{i + 1}</span>
                </div>
              </Marker>
            );
          })}

          {activePopup && !zoomLocked && (
            <Popup
              longitude={activePopup.lon}
              latitude={activePopup.lat}
              closeButton={false}
              closeOnClick={false}
              offset={12}
              anchor="top"
              className="demo-hover-popup"
            >
              <div className="demo-hover-title">{activePopup.title}</div>
              <div className="demo-hover-sub">{activePopup.sub}</div>
            </Popup>
          )}
        </Map>
      </div>

      {zoomLocked && (
        <div className="demo-zoom-lock-overlay" role="status">
          <div className="demo-zoom-lock-card">
            <div className="demo-zoom-lock-title">Book a demo to zoom in</div>
            <div className="demo-zoom-lock-sub">
              The full version unlocks parcel detail at any zoom level.
            </div>
            <div className="demo-zoom-lock-actions">
              <button
                type="button"
                className="demo-zoom-lock-secondary"
                onClick={zoomOut}
              >
                ← Zoom out
              </button>
              <a
                className="demo-zoom-lock-cta"
                href="https://calendly.com/benjamin-respire/seidr"
                target="_top"
                rel="noopener noreferrer"
                onClick={() => track('cta_clicked', { location: 'zoom_lock' })}
              >
                Book a demo →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DemoMap;
